import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { McpServerPicker } from "@arcadeai/ui-kit/mcp-server-picker";
import { Suspense, useLayoutEffect, useMemo } from "react";
import { type Control, useFormContext, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { WarningMissingRequirements } from "@/app/tools/components/warning-missing-requirements";
import { useToolkitPickerData } from "@/hooks/use-toolkit-picker-data";
import type { McpServerCreateRequest } from "../../types";
import {
  countUnavailableTools,
  indexToolsByLowerQualifiedName,
  resolveSelectedTools,
  toolkitCollectionToMcpServers,
} from "../lib/to-mcp-servers";

type McpServersFieldProps = {
  control: Control<McpServerCreateRequest>;
};

const sameOrder = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/**
 * The gateway's tool picker. Renders the ui-kit McpServerPicker over the project
 * toolkit catalog, bound to `tool_filter.allowed_tools` (version-less qualified
 * names). The picker is a full-height shell, so it fills the bounded container.
 *
 * Below it, warn about any selected tool whose secret/OAuth setup is incomplete
 * — the picker itself doesn't surface requirements.
 */
export function McpServersField({ control }: McpServersFieldProps) {
  const { setValue } = useFormContext<McpServerCreateRequest>();
  const { data: collection } = useToolkitPickerData();
  const servers = useMemo(
    () => toolkitCollectionToMcpServers(collection),
    [collection]
  );
  // Index the catalog by lower-cased version-less qualified name so a stored
  // allow-list — which can carry differently-cased or legacy versioned names —
  // resolves to the catalog's canonical tools.
  const toolIndex = useMemo(
    () => indexToolsByLowerQualifiedName(collection),
    [collection]
  );

  const storedAllowedTools = useWatch({
    control,
    name: "tool_filter.allowed_tools",
  });

  const resolvedTools = useMemo(
    () => resolveSelectedTools(toolIndex, storedAllowedTools ?? []),
    [toolIndex, storedAllowedTools]
  );
  const selectedFqns = useMemo(
    () => resolvedTools.map((tool) => tool.qualified_name),
    [resolvedTools]
  );

  // Reconcile the stored allow-list to the catalog's canonical names once the
  // catalog is known: legacy/differently-cased entries are rewritten and tools
  // no longer in the catalog are dropped, so what's saved matches what the
  // picker shows. `shouldDirty: false` keeps an untouched edit from looking
  // modified. A user edit already emits canonical names, so this is a no-op then.
  //
  // Layout effect (pre-paint) so the form field holds canonical names before the
  // picker is ever visible to click — a submit can't POST unreconciled names.
  useLayoutEffect(() => {
    if (sameOrder(storedAllowedTools ?? [], selectedFqns)) {
      return;
    }
    setValue("tool_filter.allowed_tools", selectedFqns, {
      shouldDirty: false,
      shouldValidate: false,
    });

    // Tools present in the request/saved gateway but absent from the catalog are
    // silently unrepresentable in the picker; tell the user rather than dropping
    // them without a trace (e.g. a mistyped `?tools=` deep link). Count only the
    // genuinely unavailable names — collapsing duplicate entries shrinks the list
    // too, and that is not a removal worth warning about. Writing the canonical
    // list above makes the next run a no-op, so this fires once per real drop.
    const droppedCount = countUnavailableTools(
      toolIndex,
      storedAllowedTools ?? []
    );
    if (droppedCount > 0) {
      toast.warning(
        droppedCount === 1
          ? "1 selected tool isn't available here and was removed."
          : `${droppedCount} selected tools aren't available here and were removed.`
      );
    }
  }, [storedAllowedTools, selectedFqns, setValue, toolIndex]);

  return (
    <FormField
      control={control}
      name="tool_filter.allowed_tools"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <div className="h-[19rem] xl:h-[22rem]">
              <McpServerPicker
                onChange={field.onChange}
                selectedFqns={selectedFqns}
                servers={servers}
              />
            </div>
          </FormControl>
          {resolvedTools.length > 0 ? (
            // Isolate the requirements warning in its own boundary: it reads
            // secrets/OAuth via suspense queries that first fetch when a tool is
            // selected. Without this, that suspend bubbles to the picker's outer
            // boundary and flashes the whole picker skeleton on every selection.
            <Suspense fallback={null}>
              <WarningMissingRequirements
                className="mt-3"
                tools={resolvedTools}
              />
            </Suspense>
          ) : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
